import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './layout/Layout';
import { notificationApi, pushApi } from '../services/api';
import { disableWebPush, enableWebPush, getWebPushStatus } from '../services/pushApi';
import { resolveSafeAppPath } from '../utils/safeAppPath';
import './Notifications.css';

const PUSH_CATEGORIES = [
  {
    category: 'transactional',
    label: 'Transactional',
    description: 'Booking, room, and account workflow updates.',
    locked: false,
  },
  {
    category: 'reminder',
    label: 'Reminders',
    description: 'Upcoming room bookings, schedules, and time-sensitive reminders.',
    locked: false,
  },
  {
    category: 'informational',
    label: 'Informational',
    description: 'General StudioAEye announcements and low-priority updates.',
    locked: false,
  },
  {
    category: 'security',
    label: 'Security',
    description: 'New sign-ins, password changes, and device trust changes.',
    locked: true,
  },
  {
    category: '2sv-challenge',
    label: '2SV Challenge',
    description: 'Push approvals used to complete protected sign-in and step-up flows.',
    locked: true,
  },
];

const Notifications = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread
  const [pushStatus, setPushStatus] = useState({
    supported: true,
    secureContext: true,
    permission: 'default',
    enabled: false,
  });
  const [pushLoading, setPushLoading] = useState(true);
  const [pushEnabling, setPushEnabling] = useState(false);
  const [pushDisabling, setPushDisabling] = useState(false);
  const [pushError, setPushError] = useState('');
  const [preferences, setPreferences] = useState({});
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [preferencesError, setPreferencesError] = useState('');
  const [preferencesMessage, setPreferencesMessage] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testCooldownUntil, setTestCooldownUntil] = useState(0);
  const [testMessage, setTestMessage] = useState('');

  const refreshPushStatus = async () => {
    try {
      setPushLoading(true);
      const nextStatus = await getWebPushStatus();
      setPushStatus(nextStatus);
    } catch (err) {
      console.error('Failed to read web push status:', err);
      setPushStatus({
        supported: false,
        secureContext: false,
        permission: 'unsupported',
        enabled: false,
      });
    } finally {
      setPushLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (filter === 'unread') {
        params.is_read = false;
      }
      
      const response = await notificationApi.getNotifications(params);
      setNotifications(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const normalizePreference = (preference = {}) => ({
    category: preference.category,
    push_enabled: preference.push_enabled !== false,
    quiet_hours_start: preference.quiet_hours_start || '',
    quiet_hours_end: preference.quiet_hours_end || '',
    quiet_hours_tz: preference.quiet_hours_tz || 'Europe/Belfast',
  });

  const loadPreferences = async () => {
    try {
      setPreferencesLoading(true);
      setPreferencesError('');
      const response = await pushApi.getPreferences();
      const rows = response.data?.data?.preferences || [];
      const nextPreferences = {};
      rows.forEach((preference) => {
        nextPreferences[preference.category] = normalizePreference(preference);
      });
      PUSH_CATEGORIES.forEach(({ category }) => {
        if (!nextPreferences[category]) {
          nextPreferences[category] = normalizePreference({ category });
        }
      });
      setPreferences(nextPreferences);
    } catch (err) {
      console.error('Failed to load push preferences:', err);
      setPreferencesError('Failed to load notification preferences.');
    } finally {
      setPreferencesLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  useEffect(() => {
    refreshPushStatus();
    loadPreferences();
  }, []);

  useEffect(() => {
    if (!testCooldownUntil) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (Date.now() >= testCooldownUntil) {
        setTestCooldownUntil(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [testCooldownUntil]);

  const handleEnableAlerts = async () => {
    try {
      setPushError('');
      setPushEnabling(true);
      await enableWebPush();
      await refreshPushStatus();
    } catch (err) {
      console.error('Failed to enable browser alerts:', err);
      setPushError(err.message || 'Failed to turn on browser alerts.');
      await refreshPushStatus();
    } finally {
      setPushEnabling(false);
    }
  };

  const handleDisableAlerts = async () => {
    try {
      setPushError('');
      setPushDisabling(true);
      await disableWebPush();
      await refreshPushStatus();
    } catch (err) {
      console.error('Failed to remove browser alerts:', err);
      setPushError(err.message || 'Failed to remove browser alerts from this browser.');
      await refreshPushStatus();
    } finally {
      setPushDisabling(false);
    }
  };

  const updatePreference = async (category, patch) => {
    const current = preferences[category] || normalizePreference({ category });
    const nextPreference = { ...current, ...patch, category };

    setPreferences((prev) => ({
      ...prev,
      [category]: nextPreference,
    }));
    setPreferencesError('');
    setPreferencesMessage('');

    try {
      const response = await pushApi.updatePreferences([{
        category: nextPreference.category,
        push_enabled: nextPreference.push_enabled,
        quiet_hours_start: nextPreference.quiet_hours_start || null,
        quiet_hours_end: nextPreference.quiet_hours_end || null,
        quiet_hours_tz: nextPreference.quiet_hours_tz || null,
      }]);
      const rows = response.data?.data?.preferences || [];
      const updated = rows.find((preference) => preference.category === category);
      if (updated) {
        setPreferences((prev) => ({
          ...prev,
          [category]: normalizePreference(updated),
        }));
      }
      setPreferencesMessage('Notification preferences saved.');
    } catch (err) {
      console.error('Failed to update push preferences:', err);
      setPreferences((prev) => ({
        ...prev,
        [category]: current,
      }));
      setPreferencesError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to save notification preferences.');
    }
  };

  const sendTestNotification = async () => {
    try {
      setTestSending(true);
      setTestMessage('');
      await pushApi.sendTest();
      setTestCooldownUntil(Date.now() + 60000);
      setTestMessage('Test notification requested. It should arrive shortly.');
    } catch (err) {
      const code = err.response?.data?.error?.code || err.response?.data?.code;
      if (err.response?.status === 429 || code === 'PUSH_TEST_RATE_LIMIT') {
        setTestCooldownUntil(Date.now() + 60000);
        setTestMessage('Please wait a minute before sending another test notification.');
        return;
      }
      setTestMessage(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to send test notification.');
    } finally {
      setTestSending(false);
    }
  };

  const renderPushBanner = () => {
    if (pushLoading) {
      return null;
    }

    let description = 'Turn on critical Chrome alerts for important security and 2SV notifications when StudioAEye is not open.';
    let tone = 'info';

    if (!pushStatus.supported) {
      tone = 'warning';
      description = pushStatus.secureContext
        ? 'This browser cannot receive critical Chrome alerts.'
        : 'Critical Chrome alerts require HTTPS or localhost before Web Push can be enabled.';
    } else if (pushStatus.permission === 'denied') {
      tone = 'warning';
      description = 'Critical Chrome alerts are blocked. Enable notifications in your browser settings and reload this page.';
    } else if (pushStatus.enabled) {
      tone = 'success';
      description = 'Critical Chrome alerts are active on this browser.';
    }

    return (
      <section className={`push-banner push-banner-${tone}`} data-testid="critical-chrome-alerts">
        <div>
          <h2>Critical Chrome Alerts</h2>
          <p data-testid="critical-chrome-alerts-status">{description}</p>
          <p className="push-banner-detail">Only security, 2SV, and approved critical operational alerts use this browser-level path.</p>
          {pushError && <p className="push-banner-error">{pushError}</p>}
        </div>
        <div className="push-banner-actions">
          {pushStatus.supported && pushStatus.permission !== 'denied' && !pushStatus.enabled && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEnableAlerts}
              disabled={pushEnabling}
              data-testid="critical-chrome-alerts-enable"
            >
              {pushEnabling ? 'Turning on alerts...' : 'Turn on critical Chrome alerts'}
            </button>
          )}
          {pushStatus.supported && pushStatus.enabled && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDisableAlerts}
              disabled={pushDisabling}
              data-testid="critical-chrome-alerts-remove"
            >
              {pushDisabling ? 'Removing...' : 'Remove this browser'}
            </button>
          )}
        </div>
      </section>
    );
  };

  const renderPreferences = () => {
    if (preferencesLoading) {
      return <div className="loading">Loading notification preferences...</div>;
    }

    const cooldownRemaining = testCooldownUntil ? Math.max(0, Math.ceil((testCooldownUntil - Date.now()) / 1000)) : 0;

    return (
      <section className="notification-settings-panel">
        <div className="settings-panel-header">
          <div>
            <h2>Notification Preferences</h2>
            <p>Choose which browser alerts can interrupt you. Security and 2SV alerts are required.</p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={sendTestNotification}
            disabled={testSending || cooldownRemaining > 0}
          >
            {testSending ? 'Sending...' : cooldownRemaining > 0 ? `Wait ${cooldownRemaining}s` : 'Send test notification'}
          </button>
        </div>

        {preferencesError && <div className="settings-message settings-error">{preferencesError}</div>}
        {preferencesMessage && <div className="settings-message settings-success">{preferencesMessage}</div>}
        {testMessage && <div className="settings-message settings-info">{testMessage}</div>}

        <div className="preference-list">
          {PUSH_CATEGORIES.map(({ category, label, description, locked }) => {
            const preference = preferences[category] || normalizePreference({ category });
            return (
              <article key={category} className="preference-row">
                <div>
                  <h3>{label}</h3>
                  <p>{description}</p>
                  {locked && (
                    <span className="locked-note" title="Required for account security — cannot be turned off.">
                      Required for account security — cannot be turned off.
                    </span>
                  )}
                </div>
                <label className="toggle-control">
                  <input
                    type="checkbox"
                    checked={preference.push_enabled}
                    disabled={locked}
                    onChange={(event) => updatePreference(category, { push_enabled: event.target.checked })}
                  />
                  <span>{preference.push_enabled ? 'On' : 'Off'}</span>
                </label>
              </article>
            );
          })}
        </div>

        <div className="quiet-hours-grid">
          <label>
            Quiet hours start
            <input
              type="time"
              value={preferences.transactional?.quiet_hours_start || ''}
              onChange={(event) => updatePreference('transactional', { quiet_hours_start: event.target.value || null })}
            />
          </label>
          <label>
            Quiet hours end
            <input
              type="time"
              value={preferences.transactional?.quiet_hours_end || ''}
              onChange={(event) => updatePreference('transactional', { quiet_hours_end: event.target.value || null })}
            />
          </label>
          <label>
            Time zone
            <input
              type="text"
              value={preferences.transactional?.quiet_hours_tz || 'Europe/Belfast'}
              onChange={(event) => updatePreference('transactional', { quiet_hours_tz: event.target.value })}
            />
          </label>
        </div>
      </section>
    );
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationApi.markAsRead(id);
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      // If filtering by unread, remove it
      if (filter === 'unread') {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      if (filter === 'unread') {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this notification?')) return;
    
    try {
      await notificationApi.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getIcon = (type) => {
    if (String(type || '').startsWith('network_')) {
      return '🛜';
    }

    switch (type) {
      case 'booking_request': return '📅';
      case 'booking_approved': return '✅';
      case 'booking_rejected': return '❌';
      case 'user_onboarding': return '👤';
      case 'document_expiring': return '📄';
      case 'warning': return '⚠️';
      case 'error': return '🚨';
      case 'success': return '🎉';
      default: return '🔔';
    }
  };

  const handleOpenNotification = (notification, event) => {
    event.stopPropagation();
    if (!notification.url) {
      return;
    }

    // T-NOTIF-012: same-origin application paths only; anything else opens nothing.
    const safePath = resolveSafeAppPath(notification.url);
    if (safePath) {
      navigate(safePath);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const content = (
      <div className="notifications-page">
        <header className="page-header">
          <h1>Notifications</h1>
          <div className="header-actions">
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread Only</option>
            </select>
            <button onClick={handleMarkAllRead} className="btn btn-secondary">
              Mark All Read
            </button>
          </div>
        </header>

        {renderPushBanner()}
        {renderPreferences()}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔕</span>
            <p>No notifications found</p>
          </div>
        ) : (
          <div className="notifications-list-full">
            {notifications.map(notification => (
              <div 
                key={notification.id} 
                className={`notification-card ${notification.is_read ? 'read' : 'unread'}`}
                onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
              >
                <div className="notification-icon-large">
                  {getIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-header-row">
                    <h3>{notification.title}</h3>
                    <span className="notification-time">{formatDate(notification.created_at)}</span>
                  </div>
                  <p>{notification.text}</p>
                  {notification.url && (
                    <button
                      type="button"
                      className="notification-link"
                      onClick={(e) => handleOpenNotification(notification, e)}
                    >
                      View Details →
                    </button>
                  )}
                </div>
                <button 
                  className="delete-btn"
                  onClick={(e) => handleDelete(notification.id, e)}
                  title="Delete"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default Notifications;
